import { Link } from 'react-router-dom';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Instagram, 
  Youtube, 
  Linkedin,
  ArrowRight
} from 'lucide-react';
import { Button } from '../ui/button';
import { useLanguage } from '../../contexts/LanguageContext';
import WhatsAppIcon from '../ui/WhatsAppIcon';

const LOGO_WHITE_URL = 'https://customer-assets.emergentagent.com/job_holder-enterprise/artifacts/7qu8wbdy_holdergroupbranco.png';

const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  const offices = [
    {
      city: 'Sao Paulo',
      cityDisplay: 'São Paulo',
      isMatriz: true,
      address: 'Avenida Moema, 87 - Bl A, Sala 152 - Moema',
      cep: '04077-020'
    },
    {
      city: 'Porto Alegre',
      cityDisplay: 'Porto Alegre',
      isMatriz: false,
      address: 'R. Dom Pedro II, 1240 - São João sala 406',
      cep: '90550-141'
    },
    {
      city: 'Curitiba',
      cityDisplay: 'Curitiba',
      isMatriz: false,
      address: 'Av. Iguacu, 2820 - Batel',
      cep: '80240-030'
    }
  ];

  const services = [
    { nameKey: 'services.fiscalManagement', path: '/gestao-fiscal' },
    { nameKey: 'services.positiveTax', path: '/gestao-positiva' },
    { nameKey: 'services.liabilities', path: '/gestao-passivos' },
    { nameKey: 'services.ma', path: '/ma' },
    { nameKey: 'services.credit', path: '/credito-operacoes' },
    { nameKey: 'services.legal', path: '/assessoria-juridica' },
  ];

  const quickLinks = [
    { nameKey: 'footer.aboutUs', path: '/sobre' },
    { nameKey: 'footer.services', path: '/servicos' },
    { nameKey: 'footer.partnerProgram', path: '/parceiros' },
    { nameKey: 'footer.contact', path: '/contato' },
  ];

  return (
    <footer className="bg-emerald text-white" data-testid="footer">
      {/* CTA Section */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div className="max-w-xl">
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4">
                {t('footer.ctaTitle')}
              </h2>
              <p className="text-stone-300 text-lg">
                {t('footer.ctaDesc')}
              </p>
            </div>
            <Button 
              asChild
              className="bg-bronze hover:bg-bronze-light text-white rounded-sm px-8 py-6 text-lg font-serif btn-shine"
              data-testid="footer-cta-btn"
            >
              <Link to="/contato" className="flex items-center gap-2">
                {t('footer.ctaBtn')}
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6" data-testid="footer-logo">
              <img 
                src={LOGO_WHITE_URL} 
                alt="Holder & Co. Group" 
                className="h-12 w-auto"
              />
            </Link>
            <p className="text-stone-400 mb-6 text-sm leading-relaxed">
              {t('footer.about')}
            </p>
            <div className="flex gap-4">
              <a 
                href="https://instagram.com/holder.corp" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-sm bg-white/10 flex items-center justify-center hover:bg-bronze transition-colors"
                data-testid="footer-instagram"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a 
                href="https://youtube.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-sm bg-white/10 flex items-center justify-center hover:bg-bronze transition-colors"
                data-testid="footer-youtube"
                aria-label="YouTube"
              >
                <Youtube className="h-5 w-5" />
              </a>
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-sm bg-white/10 flex items-center justify-center hover:bg-bronze transition-colors"
                data-testid="footer-linkedin"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h3 className="text-lg font-serif font-semibold mb-6">{t('footer.services')}</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.path + service.nameKey}>
                  <Link 
                    to={service.path}
                    className="text-stone-400 hover:text-bronze transition-colors text-sm"
                    data-testid={`footer-service-${service.path.replace('/', '')}`}
                  >
                    {t(service.nameKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links Column */}
          <div>
            <h3 className="text-lg font-serif font-semibold mb-6">{t('footer.quickLinks')}</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-stone-400 hover:text-bronze transition-colors text-sm"
                    data-testid={`footer-link-${link.path.replace('/', '')}`}
                  >
                    {t(link.nameKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h3 className="text-lg font-serif font-semibold mb-6">{t('footer.contact')}</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-bronze mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white text-sm font-medium">{t('footer.phone')}</p>
                  <div className="flex items-center gap-2">
                    <a
                      href="tel:+5511921313708"
                      className="text-stone-400 hover:text-bronze transition-colors text-sm"
                      data-testid="footer-phone"
                    >
                      +55 (11) 921 313 708
                    </a>
                    <a
                      href="https://wa.me/5511921313708"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#25D366] hover:opacity-80 transition-opacity"
                      data-testid="footer-whatsapp"
                      aria-label="WhatsApp"
                    >
                      <WhatsAppIcon className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-bronze mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white text-sm font-medium">{t('footer.email')}</p>
                  <a 
                    href="mailto:contato@holdcorp.com.br" 
                    className="text-stone-400 hover:text-bronze transition-colors text-sm"
                    data-testid="footer-email"
                  >
                    contato@holdcorp.com.br
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Offices */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <h3 className="text-lg font-serif font-semibold mb-6">{t('footer.offices')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {offices.map((office) => (
              <div 
                key={office.city}
                className="flex items-start gap-3 p-4 rounded-sm bg-white/5"
                data-testid={`footer-office-${office.city.toLowerCase().replace(/\s/g, '-')}`}
              >
                <MapPin className="h-5 w-5 text-bronze mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white font-medium text-sm">
                    {office.cityDisplay} {office.isMatriz ? `(${t('about.offices.matriz')})` : ''}
                  </p>
                  <p className="text-stone-400 text-sm">{office.address}</p>
                  <p className="text-stone-400 text-sm">{office.cep}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-stone-400">
            <p>&copy; {currentYear} Holder & Co. Group - {t('footer.rights')}</p>
            <div className="flex items-center gap-2 text-xs">
              <span>CNPJ: 63.189.436/0001-90</span>
              <span className="hidden md:inline">|</span>
              <span>{t('footer.compliance')}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
